import { storage, db } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';

export interface ImageUploadOptions {
  userId: string;
  file: File;
  folder?: string;
  maxSizeKB?: number;
  allowedTypes?: string[];
}

export interface ImageUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

/**
 * Validates image file before upload
 */
export const validateImageFile = (
  file: File,
  maxSizeKB: number = 2048, // 2MB default
  allowedTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
): { isValid: boolean; error?: string } => {
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `File type not supported. Please use: ${allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')}`
    };
  }

  // Check file size
  const fileSizeKB = file.size / 1024;
  if (fileSizeKB > maxSizeKB) {
    return {
      isValid: false,
      error: `File size too large. Maximum size: ${maxSizeKB}KB (${Math.round(maxSizeKB / 1024)}MB)`
    };
  }

  return { isValid: true };
};

/**
 * Uploads profile image to Firebase Storage and updates user document
 */
export const uploadProfileImage = async (options: ImageUploadOptions): Promise<ImageUploadResult> => {
  const {
    userId,
    file,
    folder = 'profile-images',
    maxSizeKB = 2048,
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  } = options;

  try {
    // Validate file
    const validation = validateImageFile(file, maxSizeKB, allowedTypes);
    if (!validation.isValid) {
      return {
        success: false,
        error: validation.error
      };
    }

    // Generate unique filename with timestamp
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${userId}_${timestamp}.${fileExtension}`;
    const filePath = `${folder}/${fileName}`;

    console.log(`📸 Uploading profile image: ${filePath}`);

    // Create storage reference
    const storageRef = ref(storage, filePath);

    // Upload file
    const uploadResult = await uploadBytes(storageRef, file);
    console.log('✅ File uploaded successfully');

    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    console.log('✅ Download URL generated:', downloadURL);

    // Update user document with new profile image URL
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      profileImage: downloadURL,
      profileImagePath: filePath, // Store path for potential deletion
      lastUpdatedAt: new Date()
    });

    console.log('✅ User document updated with new profile image URL');

    return {
      success: true,
      url: downloadURL
    };

  } catch (error) {
    console.error('❌ Error uploading profile image:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload image'
    };
  }
};

/**
 * Deletes old profile image from storage (useful when updating)
 */
export const deleteProfileImage = async (imagePath: string): Promise<boolean> => {
  try {
    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
    console.log('✅ Old profile image deleted:', imagePath);
    return true;
  } catch (error) {
    console.error('❌ Error deleting old profile image:', error);
    return false;
  }
};

/**
 * Uploads and replaces profile image (deletes old one first)
 */
export const replaceProfileImage = async (
  options: ImageUploadOptions,
  oldImagePath?: string
): Promise<ImageUploadResult> => {
  try {
    // Upload new image first
    const uploadResult = await uploadProfileImage(options);

    if (uploadResult.success && oldImagePath) {
      // Delete old image after successful upload
      await deleteProfileImage(oldImagePath);
    }

    return uploadResult;
  } catch (error) {
    console.error('❌ Error replacing profile image:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to replace image'
    };
  }
};

/**
 * Compresses image before upload to ensure it fits within size limits
 * Uses progressive quality reduction if needed to get under 2MB
 * Handles both File objects and base64 data URLs (from Capacitor Camera)
 */
export const compressImage = (input: File | string, quality: number = 0.8, maxSizeKB: number = 2048): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    const img = new Image();

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.onload = () => {
      // Calculate new dimensions (max 800px for aggressive compression)
      const maxSize = 800;
      let { width, height } = img;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image
      ctx.drawImage(img, 0, 0, width, height);

      // Get filename for the compressed file
      const fileName = typeof input === 'string'
        ? `compressed_${Date.now()}.jpg`
        : input.name.replace(/\.[^.]+$/, '.jpg'); // Replace extension with .jpg

      // Helper function to try compression at a given quality
      const tryCompress = (attemptQuality: number, attempt: number = 1): void => {
        console.log(`📸 Compression attempt ${attempt} with quality ${attemptQuality.toFixed(1)}`);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create compressed image blob'));
              return;
            }

            const compressedFile = new File([blob], fileName, {
              type: 'image/jpeg', // Always convert to JPEG for better compression
              lastModified: Date.now()
            });

            const compressedSizeKB = compressedFile.size / 1024;
            console.log(`📸 Attempt ${attempt} result: ${Math.round(compressedSizeKB)}KB`);

            // If still too large and we can reduce quality further, try again
            if (compressedSizeKB > maxSizeKB && attemptQuality > 0.2) {
              tryCompress(attemptQuality - 0.15, attempt + 1); // Reduce quality more aggressively
            } else if (compressedSizeKB > maxSizeKB) {
              // Even at minimum quality, still too large
              reject(new Error(`Image too large even after maximum compression: ${Math.round(compressedSizeKB)}KB`));
            } else {
              resolve(compressedFile);
            }
          },
          'image/jpeg', // Always use JPEG for better compression
          attemptQuality
        );
      };

      // Start compression
      tryCompress(quality, 1);
    };

    // Handle both File objects and base64 data URLs
    if (typeof input === 'string') {
      // Base64 data URL from Capacitor Camera
      img.src = input;
    } else {
      // File object from HTML input
      img.src = URL.createObjectURL(input);
    }
  });
};